/**
 * Enum values shared by the API (Prisma) and the SPA. These strings are
 * persisted, so renaming one is a migration, not a refactor.
 */

/**
 * What a user is *allowed to do*. Deliberately separate from their job title,
 * which lives in the Designation table ("Recruiter", "Senior Engineer", ...).
 *
 * MANAGER is a permission tier only. The reviewer for the manager stage of an
 * assessment is always the employee's reportingManagerId - never "any user
 * whose role is MANAGER", which would let any manager review any employee.
 */
export const ROLES = ['EMPLOYEE', 'MANAGER', 'DEPT_HEAD', 'HR', 'SUPER_ADMIN'] as const;
export type Role = (typeof ROLES)[number];

/** Ascending privilege. Used for "at least this role" checks, not for review routing. */
export const ROLE_RANK: Record<Role, number> = {
  EMPLOYEE: 0,
  MANAGER: 1,
  DEPT_HEAD: 2,
  HR: 3,
  SUPER_ADMIN: 4,
};

export const ROLE_LABELS: Record<Role, string> = {
  EMPLOYEE: 'Employee',
  MANAGER: 'Reporting Manager',
  DEPT_HEAD: 'Department Head',
  HR: 'HR',
  // The top tier, and the only one with no ceiling above it. Labelled for both
  // things it is: the role that signs off the Management stage of the review chain,
  // and the administrator of the system. Plain "Management" read like a middle tier
  // to the person holding it, which is the last impression an admin role should give.
  SUPER_ADMIN: 'Management (Admin)',
};

/** Roles permitted to onboard employees, change roles and control quarter windows. */
export const PEOPLE_ADMIN_ROLES: readonly Role[] = ['HR', 'SUPER_ADMIN'];

export function hasAtLeastRole(actual: Role, required: Role): boolean {
  return ROLE_RANK[actual] >= ROLE_RANK[required];
}

/**
 * ORGANIZATIONAL goals are assigned by the reporting manager, carry weightage
 * totalling 100, and drive the official rating.
 * INDIVIDUAL goals are created by the employee, always carry 0 weightage, and
 * surface only during appraisal as supporting evidence.
 */
export const GOAL_TYPES = ['ORGANIZATIONAL', 'INDIVIDUAL'] as const;
export type GoalType = (typeof GOAL_TYPES)[number];

export const EMPLOYEE_STATUSES = [
  'PENDING_ACCEPTANCE',
  'ACCEPTED',
  'ACTIVE',
  'ON_PIP',
  'EXITED',
] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export const GOAL_ASSIGNMENT_STATUSES = ['DRAFT', 'PENDING_ACCEPTANCE', 'ACCEPTED'] as const;
export type GoalAssignmentStatus = (typeof GOAL_ASSIGNMENT_STATUSES)[number];

export const CYCLE_STATUSES = ['UPCOMING', 'ACTIVE', 'CLOSED'] as const;
export type CycleStatus = (typeof CYCLE_STATUSES)[number];

export const QUARTER_STATUSES = ['UPCOMING', 'IN_PROGRESS', 'IN_REVIEW', 'CLOSED'] as const;
export type QuarterStatus = (typeof QUARTER_STATUSES)[number];

/**
 * HR may force a quarter's self-assessment window open or shut regardless of
 * the calendar. NONE means "follow the spec calendar".
 */
export const WINDOW_OVERRIDES = ['NONE', 'FORCED_OPEN', 'FORCED_CLOSED'] as const;
export type WindowOverride = (typeof WINDOW_OVERRIDES)[number];

export const RECOGNITION_TYPES = [
  'STAR_PERFORMER',
  'BEST_TEAM_PLAYER',
  'INNOVATION_AWARD',
  'CUSTOMER_HERO',
  'KNOWLEDGE_SHARING',
  'EMPLOYEE_OF_THE_QUARTER',
  'EMPLOYEE_OF_THE_YEAR',
  'SPOT_AWARD',
] as const;
export type RecognitionType = (typeof RECOGNITION_TYPES)[number];

export const RECOGNITION_LABELS: Record<RecognitionType, { label: string; emoji: string }> = {
  STAR_PERFORMER: { label: 'Star Performer', emoji: '🏆' },
  BEST_TEAM_PLAYER: { label: 'Best Team Player', emoji: '🌟' },
  INNOVATION_AWARD: { label: 'Innovation Award', emoji: '🚀' },
  CUSTOMER_HERO: { label: 'Customer Hero', emoji: '❤️' },
  // Peer tier: anybody may thank anybody for teaching the room. Also what a Dev
  // Connect presenter receives automatically once the session is recorded as held.
  KNOWLEDGE_SHARING: { label: 'Knowledge Sharing', emoji: '🎤' },
  EMPLOYEE_OF_THE_QUARTER: { label: 'Employee of the Quarter', emoji: '🥇' },
  EMPLOYEE_OF_THE_YEAR: { label: 'Employee of the Year', emoji: '👑' },
  SPOT_AWARD: { label: 'Spot Award', emoji: '💎' },
};

/**
 * Two kinds of recognition, and the difference is who may grant it.
 *
 * APPRECIATION is peer-to-peer: anyone thanking anyone, which is the whole point of
 * it and why it stays open. OFFICIAL is the company speaking - the awards that get
 * announced, sit on an appraisal, and attach to a bonus - so it is granted by HR, or
 * by that employee's own reporting manager.
 *
 * The tier is DERIVED from the type, never stored alongside it. A stored tier could
 * disagree with the award it is attached to, and then a peer thank-you and an
 * Employee of the Year would be indistinguishable to whoever had to sort it out.
 *
 * Neither tier feeds any calculation. Official recognition appears on an appraisal
 * as evidence a human reads, not as points a rating is computed from: the moment it
 * counts towards a score it becomes currency, people farm it, and the award stops
 * meaning what its name says.
 */
export const RECOGNITION_TIERS = ['APPRECIATION', 'OFFICIAL'] as const;
export type RecognitionTier = (typeof RECOGNITION_TIERS)[number];

const OFFICIAL_RECOGNITION_TYPES = new Set<RecognitionType>([
  'EMPLOYEE_OF_THE_QUARTER',
  'EMPLOYEE_OF_THE_YEAR',
  'SPOT_AWARD',
]);

export function recognitionTier(type: RecognitionType): RecognitionTier {
  return OFFICIAL_RECOGNITION_TYPES.has(type) ? 'OFFICIAL' : 'APPRECIATION';
}

export function isOfficialRecognition(type: RecognitionType): boolean {
  return recognitionTier(type) === 'OFFICIAL';
}

export function recognitionTypesFor(tier: RecognitionTier): RecognitionType[] {
  return RECOGNITION_TYPES.filter((t) => recognitionTier(t) === tier);
}

export const RECOGNITION_TIER_LABELS: Record<RecognitionTier, { label: string; blurb: string }> = {
  APPRECIATION: {
    label: 'Appreciation',
    blurb: 'Anyone can send one. It carries no rating and never affects an appraisal.',
  },
  OFFICIAL: {
    label: 'Official award',
    blurb: 'Granted by HR or the reporting manager. Appears on the appraisal as evidence.',
  },
};

/** Attachment owners. Evidence is attached to KRAs, goals and meeting notes. */
export const ATTACHMENT_ENTITIES = ['KRA_RATING', 'GOAL', 'ASSESSMENT', 'ONE_ON_ONE'] as const;
export type AttachmentEntity = (typeof ATTACHMENT_ENTITIES)[number];
